import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { StudentHonorsSection, StudentHonor } from '../../../types';
import { apiClient } from '../../../services/api/apiClient';

interface StudentShowcaseProps {
  data?: StudentHonorsSection;
  fallbackStudents?: StudentHonorsSection['students'];
}

// Fallback student honors data when API response is null or invalid
const DEFAULT_STUDENT_HONORS: StudentHonorsSection = {
  title: 'Câu Chuyện Thành Công Của Học Viên',
  displayCount: 6,
  students: [
    {
      _id: 'student-1',
      name: 'Nguyễn Thị Hạnh',
      level: 'Band 8.0',
      profileImage: '/images/student-1.jpg',
      honorDescription: 'Đạt Band 8.0 sau 3 tháng học tập với kỳ lực',
      isBronze: false,
    },
    {
      _id: 'student-2',
      name: 'Trần Minh Huy',
      level: 'Band 7.5',
      profileImage: '/images/student-2.jpg',
      honorDescription: 'Cải thiện từ Band 6.0 lên Ban 7.5 trong 2 tháng',
      isBronze: true,
    },
    {
      _id: 'student-3',
      name: 'Phạm Lan Anh',
      level: 'Band 8.0',
      profileImage: '/images/student-3.jpg',
      honorDescription: 'Đạt mục tiêu Band 8.0 để du học Anh Quốc',
      isBronze: false,
    },
    {
      _id: 'student-4',
      name: 'Lý Hoàng Long',
      level: 'Band 7.5',
      profileImage: '/images/student-4.jpg',
      honorDescription: 'Chinh phục IELTS Speaking qua luyện tập thường xuyên',
      isBronze: true,
    },
    {
      _id: 'student-5',
      name: 'Vũ Thị Hương',
      level: 'Band 8.0',
      profileImage: '/images/student-5.jpg',
      honorDescription: 'Thành công trong kỳ thi IELTS lần đầu tiên',
      isBronze: false,
    },
    {
      _id: 'student-6',
      name: 'Đặng Quốc Tuấn',
      level: 'Band 7.0',
      profileImage: '/images/student-6.jpg',
      honorDescription: 'Đạt Band 7.0 nhờ phương pháp học hiệu quả',
      isBronze: true,
    },
  ],
};

// Modal component for student details
const StudentModal = ({ student, isOpen, onClose }: { student: any; isOpen: boolean; onClose: () => void }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!student) return null;

  return (
    <>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          {/* Modal content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden w-full max-w-3xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col md:flex-row">
              {/* Left column - Image */}
              <div className="w-full md:w-2/5 relative h-80 md:h-auto">
                <div className="relative h-full">
                  <img
                    src={student.profileImage || 'https://picsum.photos/300/450'}
                    alt={student.name}
                    className="w-full h-full object-cover"
                  />

                  {/* Black overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-70"></div>

                  {/* Student info */}
                  <div className="absolute bottom-0 inset-x-0 p-6 z-20">
                    <h2 className="text-2xl font-bold text-white mb-3">{student.name}</h2>

                    {/* Level badge */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-white/30 backdrop-blur-sm text-white">
                        {student.level || 'Học Viên Xuất Sắc'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column - Details */}
              <div className="w-full md:w-3/5 p-6 md:p-8 relative">
                {/* Close button */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  onClick={onClose}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-600 dark:text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>

                {/* Story content */}
                <div className="h-full flex flex-col">
                  <div className="mb-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#017e6e] to-emerald-400 flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                          />
                        </svg>
                      </div>
                      <h3 className="ml-3 text-xl font-bold text-gray-800 dark:text-white">Câu chuyện thành công</h3>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto max-h-80 md:max-h-[350px] pr-2">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {student.honorDescription ||
                        `${student.name} là một trong những học viên xuất sắc của chúng tôi.

Với sự nỗ lực không ngừng và tinh thần học hỏi đáng ngưỡng mộ, ${student.name} đã hoàn thành xuất sắc khóa học và đạt được mục tiêu cao.

Không chỉ có năng lực chuyên môn tốt, ${student.name} còn thể hiện tinh thần đồng đội tuyệt vời trong suốt quá trình học tập.

Chúng tôi tin rằng với những kiến thức và kỹ năng đã tích lũy được, ${student.name} sẽ có một tương lai rực rỡ và thành công.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

interface CarouselCardProps {
  student: any;
  index: number;
  onClick: () => void;
}

const CarouselCard: React.FC<CarouselCardProps> = ({ student, index, onClick }) => (
  <motion.div
    className="relative flex-shrink-0 w-[360px] bg-gray-100 dark:bg-gray-800 rounded-2xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-shadow duration-300"
    onClick={onClick}
    whileHover={{ y: -6 }}
    transition={{ type: 'spring', stiffness: 300, damping: 22 }}
  >
    {/* Image */}
    <div className="w-full h-60 overflow-hidden">
      <img
        src={student.profileImage || `https://picsum.photos/360/240?random=${index}`}
        alt={student.name}
        className="w-full h-full object-cover select-none"
        draggable={false}
      />
    </div>

    {/* Content */}
    <div className="p-5 flex flex-col gap-3">
      {/* Date */}
      <p className="text-xs text-gray-400 dark:text-gray-500">
        {student.date || '21 January 2020'}
      </p>

      {/* Title */}
      <h3 className="text-base font-bold text-gray-800 dark:text-white leading-snug line-clamp-2">
        {student.name}
      </h3>

      {/* Description */}
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
        {student.honorDescription || 'Học viên xuất sắc với thành tích nổi bật trong quá trình học tập.'}
      </p>

      {/* Read More button */}
      <button className="mt-2 self-start px-5 py-2 rounded-full border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-[#017e6e] hover:text-white hover:border-[#017e6e] transition-colors duration-200">
        Xem Chi Tiết
      </button>
    </div>
  </motion.div>
);

const StudentShowcase: React.FC<StudentShowcaseProps> = ({ data, fallbackStudents }) => {
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resolvedStudents, setResolvedStudents] = useState<StudentHonor[] | null>(null);

  const sectionRef = useRef(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const accXRef = useRef(0);
  const pausedRef = useRef(false);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });

  // Fetch students by ID list when data.students contains string IDs
  useEffect(() => {
    const students = data?.students;
    debugger;
    if (!students || students.length === 0) return;
    // If first element is a string, they are ObjectId references — fetch from API
    if (typeof (students[0] as any) === 'string') {
      const ids = students as unknown as string[];
      const limit = data?.displayCount || ids.length;
      apiClient.post<{ items: any[]; pagination: any }>(
        `api/globals/collection/students/search`,
        {
          page: 1,
          limit,
          filterGroup: {
            type: 'and',
            groups: [{ field: '_id', op: { $in: ids } }],
          },
        },
        false
      ).then((res) => {
        if (res.statusCode === 200 && res.data?.items) {
          const mapped: StudentHonor[] = res.data.items.map((item: any) => ({
            _id: String(item.id),
            name: item.data?.Name || item.data?.name || '',
            level: item.data?.Level || item.data?.level || '',
            profileImage: item.data?.Profile_Image,
            isBronze: item.data?.['Is Bronze'] ?? item.data?.isBronze ?? false,
            honorDescription: item.data?.['Honor Description'] || item.data?.honorDescription || '',
          }));
          setResolvedStudents(mapped);
        }
      }).catch(() => {
        // silently fall back to default
      });
    }
  }, [data]);

  // Use fallback if data is null or undefined
  const showcaseData = data || { ...DEFAULT_STUDENT_HONORS, students: fallbackStudents || DEFAULT_STUDENT_HONORS.students };
  // resolvedStudents takes priority (fetched by ID); fall back to whatever is in showcaseData
  const students: StudentHonor[] = resolvedStudents ?? (
    typeof (showcaseData.students?.[0] as any) === 'string'
      ? DEFAULT_STUDENT_HONORS.students
      : (showcaseData.students as StudentHonor[]) || []
  );
  const displayStudents = students.slice(0, showcaseData.displayCount || 6);
  // Double for seamless infinite loop
  const duplicated = [...displayStudents, ...displayStudents];

  // Auto-scroll carousel using rAF
  useEffect(() => {
    if (displayStudents.length === 0) return;
    let animId: number;
    let lastTs: number | null = null;
    const SPEED = 80; // px per second

    const tick = (ts: number) => {
      if (!trackRef.current) { animId = requestAnimationFrame(tick); return; }
      if (lastTs !== null && !pausedRef.current) {
        const delta = ts - lastTs;
        accXRef.current += (delta / 1000) * SPEED;
        const half = trackRef.current.scrollWidth / 2;
        if (half > 0 && accXRef.current >= half) accXRef.current -= half;
        trackRef.current.style.transform = `translateX(${-accXRef.current}px)`;
      }
      lastTs = ts;
      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayStudents.length]);

  const handlePrev = () => {
    if (!trackRef.current) return;
    const cardWidth = trackRef.current.scrollWidth / 2 / displayStudents.length;
    const half = trackRef.current.scrollWidth / 2;
    accXRef.current -= cardWidth;
    if (accXRef.current < 0) accXRef.current += half;
  };

  const handleNext = () => {
    if (!trackRef.current) return;
    const cardWidth = trackRef.current.scrollWidth / 2 / displayStudents.length;
    const half = trackRef.current.scrollWidth / 2;
    accXRef.current += cardWidth;
    if (accXRef.current >= half) accXRef.current -= half;
  };

  const openModal = (student: any) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedStudent(null), 300);
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"
      id="student-showcase"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <div className="relative py-6">
            {/* Decorative elements */}
            <div className="absolute -top-8 -left-8 w-16 h-16 rounded-full bg-gradient-to-br from-teal-300/30 to-teal-500/30 blur-xl dark:from-teal-700/30 dark:to-teal-900/30"></div>
            <div className="absolute -bottom-4 -right-4 w-12 h-12 rounded-full bg-gradient-to-tr from-emerald-400/40 to-cyan-400/40 blur-lg dark:from-emerald-700/40 dark:to-cyan-700/40"></div>

            {/* Decorative icon */}
            <motion.div
              initial={{ scale: 0, rotate: -45, opacity: 0 }}
              animate={isInView ? { scale: 1, rotate: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
              className="mx-auto mb-4 w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-400 dark:from-teal-400 dark:to-emerald-300 rounded-lg flex items-center justify-center shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-center text-2xl font-bold sm:text-3xl lg:text-4xl text-gray-800 dark:text-gray-100"
            >
              Vinh danh học viên
            </motion.h2>

            {/* Animated underline */}
            <div className="relative mx-auto mt-4" style={{ maxWidth: '280px' }}>
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={isInView ? { width: '100%', opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="h-1 sm:h-1.5 bg-gradient-to-r from-teal-500 via-emerald-400 to-cyan-400 dark:from-teal-600 dark:via-emerald-500 dark:to-cyan-500 rounded-full"
              ></motion.div>
            </div>

            {/* Decorative dots */}
            <div className="flex justify-center space-x-2 mt-4">
              {[0, 1, 2].map((dot) => (
                <motion.div
                  key={dot}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : {}}
                  transition={{
                    duration: 0.4,
                    delay: 1 + dot * 0.1,
                    type: 'spring',
                    stiffness: 400,
                    damping: 10,
                  }}
                  className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 dark:from-teal-400 dark:to-emerald-300"
                ></motion.div>
              ))}
            </div>
          </div>

          <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg px-4 sm:px-0 relative z-10 mt-4">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="block"
            >
              Mỗi cá nhân là một câu chuyện truyền cảm hứng, góp phần làm nên cộng đồng học tập xuất sắc
            </motion.span>
          </p>
        </motion.div>

      </div>

      {/* Students Carousel - full browser width */}
      {displayStudents.length > 0 ? (
        <div className="relative overflow-x-hidden mb-8 py-3">
          {/* Left button */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg flex items-center justify-center hover:bg-white dark:hover:bg-gray-700 transition-colors backdrop-blur-sm"
            onClick={handlePrev}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          {/* Right button */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg flex items-center justify-center hover:bg-white dark:hover:bg-gray-700 transition-colors backdrop-blur-sm"
            onClick={handleNext}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div
            ref={trackRef}
            className="flex gap-4 sm:gap-5 w-max px-4"
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            {duplicated.map((student, i) => (
              <CarouselCard
                key={`${student._id}-${i}`}
                student={student}
                index={i}
                onClick={() => openModal(student)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 dark:text-gray-400 py-8 mb-8">
          Chưa có bộ sưu tập học viên nào
        </div>
      )}

   

      {/* Student Modal */}
      <StudentModal student={selectedStudent} isOpen={isModalOpen} onClose={closeModal} />
    </section>
  );
};

export default StudentShowcase;
